import { useI18n } from "@core/i18n/i18n";
import { analyticsService, userStatusManager } from "@core/service-container";
import { RegistrationStatus } from "@domain/user/user";
import styled from "@emotion/styled";
import { BaseDialog, BaseDialogProps, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { useObservable } from "micro-observables";
import { Dialog } from "primereact/dialog";
import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { RoutesPath } from "@ui/navigation/routes";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import ValidateAccountIcon from "@assets/icons/validate-account.svg";
import PendingAccountIcon from "@assets/icons/pending-account.svg";
import { stack } from "@ui/style";
import { Button } from "@ui/components/buttons/button";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";

interface RestrictedDialogProps extends BaseDialogProps {
	context: "AddPatient" | "AssignTemplate";
}

export const RestrictedDialog = React.forwardRef<Dialog, PropsWithChildren<RestrictedDialogProps>>(
	({ context, onHide, children, ...otherProps }, ref) => {
		const { format } = useI18n();
		const navigate = useNavigate();
		const ptStatus = useObservable(userStatusManager.ptRegistrationStatus);
		const header = otherProps.header?.toString() ?? "";

		const handleRegistrationClick = useCallback(() => {
			analyticsService.trackEvent({
				category: AnalyticsCategory.REGISTRATION,
				action: "startVerificationFlowCTA",
				params: [header],
			});

			navigate(RoutesPath.ProfessionalInformation);
		}, [header, navigate]);

		const restrictedBaseDialogProps = useMemo(
			() => ({ ...otherProps, ref, onHide, width: DIALOG_SIZE.S, header: <></>, footer: <></> }),
			[onHide, otherProps, ref],
		);

		return useMemo(() => {
			switch (ptStatus) {
				case RegistrationStatus.INCOMPLETE:
					return (
						<RestrictedBaseDialog {...restrictedBaseDialogProps}>
							<Container>
								<ValidateAccountIcon width={128} height={128} />
								<div className="p-size-m-bold">
									{context === "AddPatient"
										? format("restrictedDialog.incompleteStatus.addPatient.title")
										: format("restrictedDialog.incompleteStatus.assignTemplate.title")}
								</div>
								<div className="p-size-s">{format("restrictedDialog.incompleteStatus.description")}</div>
								<Button onClick={handleRegistrationClick}>
									{format("restrictedDialog.incompleteStatus.validateAccountButton")}
								</Button>
								<TertiaryButton
									onClick={onHide}
									label={format("restrictedDialog.incompleteStatus.closeDialogButton")}
								/>
							</Container>
						</RestrictedBaseDialog>
					);
				case RegistrationStatus.PENDING:
					return (
						<RestrictedBaseDialog {...restrictedBaseDialogProps}>
							<Container>
								<PendingAccountIcon width={128} height={128} />
								<div className="p-size-m-bold">{format("restrictedDialog.pendingStatus.title")}</div>
								<div className="p-size-s">{format("restrictedDialog.pendingStatus.description")}</div>
								<Button uiStyle="secondary_B" onClick={onHide}>
									{format("restrictedDialog.pendingStatus.closeDialogButton")}
								</Button>
							</Container>
						</RestrictedBaseDialog>
					);
				default:
					return (
						<BaseDialog {...otherProps} ref={ref} onHide={onHide}>
							<div id="dialogContent">{children}</div>
						</BaseDialog>
					);
			}
		}, [
			children,
			context,
			format,
			handleRegistrationClick,
			onHide,
			otherProps,
			ptStatus,
			ref,
			restrictedBaseDialogProps,
		]);
	},
);

RestrictedDialog.displayName = "RestrictedDialog";

const RestrictedBaseDialog = styled(BaseDialog)`
	& .p-dialog-header {
		padding-bottom: 0;
	}

	& .p-dialog-header:after {
		display: none;
	}

	& .p-dialog-content {
		padding-top: 0;
	}
`;

const Container = styled.div`
	${stack(24, "flexstart", "center")}
	text-align: center;
`;
